import { useEffect, useMemo, useRef } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  IconLoader,
  PermissionDenied,
  PermissionEnum,
  IconExpand,
  useQueryState,
  usePrevious,
  useGetList,
  useScroll,
  useFilters,
  Table,
} from '@ui/src';
import { useAppSelector } from '@store';
import {
  deliveryChannelsListSchema,
  deliveryChannelsListFiltersSchema,
  deliveryChannelsListColumns,
} from '../variables/delivery-channel';
import {
  useLazyGetAmlDictionariesQuery,
  useLazyGetAmlBaseCurrenciesQuery,
  useLazyGetAmlCurrenciesQuery,
} from '../../../store/dictionaries/services';
import {
  DeliveryChannel,
  RiskScoreClientTypeEnum,
  RiskScoreItem,
} from '../../types';
import { DeliveryChannelListTypes } from './types';

import ROUTES from '../../../routes';

import cn from '../partial.module.css';

const formatIncorporationDatesList = (
  list: DeliveryChannel[],
): DeliveryChannel[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, React.JSX.Element> = {
      riskCategory: (
        <div>{`${item.riskCategory.charAt(0)}${item.riskCategory
          .slice(1)
          .toLowerCase()} risk`}</div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const DeliveryChannels = ({
  values,
  pagination,
  getList,
  isFetching,
  clientType,
  isLoading,
  errorsList,
}: DeliveryChannelListTypes): React.JSX.Element => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const [getDictionaries] = useLazyGetAmlDictionariesQuery();
  const [getAmlBaseCurrency] = useLazyGetAmlBaseCurrenciesQuery();
  const [getAmlCurrencies] = useLazyGetAmlCurrenciesQuery();

  const {
    amlDictionaries: {
      activePermissions,
      currencies,
      riskScoreRanges,
      baseCurrency,
    },
  } = useAppSelector((state) => state);

  const { sort, onSortChange, filters, onFilterChange } = useQueryState(
    deliveryChannelsListSchema,
  );

  const prevFilters = usePrevious(filters);

  const { getListAction, setPage, onSort, onFilter, errorList } =
    useGetList<RiskScoreItem>({
      getList,
      onFilterChange,
      onSortChange,
      schema: deliveryChannelsListSchema,
    });

  const formattedData = useMemo(() => {
    const data = formatIncorporationDatesList(values as DeliveryChannel[]);

    return data;
  }, [values, sort]);

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema: deliveryChannelsListFiltersSchema,
    filters,
    onFilterChange: onFilter,
  });

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !values &&
      sort &&
      Object.keys(sort).length
    ) {
      getListAction({
        filters,
        sort,
      });
    }
  }, [filters, prevFilters, sort, values]);

  useEffect(() => {
    if (
      activePermissions?.includes(
        PermissionEnum.SETTINGS_DELIVERY_CHANNELS_LIST,
      )
    ) {
      if (!riskScoreRanges) {
        getDictionaries();
      }

      if (!currencies) {
        getAmlCurrencies();
      }

      if (!baseCurrency) {
        getAmlBaseCurrency();
      }
    }
  }, [riskScoreRanges, currencies, activePermissions, baseCurrency]);

  let globalContent = (
    <div className={cn.deliveryChannelsLoader}>
      <IconLoader />
    </div>
  );

  let content;

  if (isLoading) {
    content = (
      <div className={cn.deliveryChannelsLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <Table
        columns={deliveryChannelsListColumns}
        data={formattedData || []}
        onSort={(name) => onSort(name)}
        filters={filters}
        sort={sort}
        columnsLabels={{}}
        onFilterChange={(filtersValues) => onFilter(filtersValues)}
        filtersComponent={filtersComponent}
        handleSubmit={handleSubmit}
        isLoading={isFetching}
        error={errorsList || errorList}
        take="30"
        setPage={(data) => setPage({ ...data, filters })}
        pagination={pagination}
        clickable
      />
    );
  }

  if (activePermissions) {
    if (
      activePermissions.includes(PermissionEnum.SETTINGS_DELIVERY_CHANNELS_LIST)
    ) {
      globalContent = (
        <div className={cn.deliveryChannels}>
          <div className={cn.deliveryChannelsBottom}>
            <div className={cn.deliveryChannelControls}>
              {activePermissions?.includes(
                PermissionEnum.SETTINGS_DELIVERY_CHANNELS_CREATE_ACTION,
              ) && (
                <NavLink
                  to={
                    clientType === RiskScoreClientTypeEnum.INDIVIDUAL
                      ? ROUTES.INDIVIDUAL_CLIENTS_RISK_DELIVERY_CHANNELS_CREATE
                      : ROUTES.CORPORATE_CLIENTS_RISK_DELIVERY_CHANNELS_CREATE
                  }
                  className={cn.deliveryChannelLink}
                >
                  <IconExpand />
                  Add Delivery channel
                </NavLink>
              )}
            </div>
            <div className={cn.deliveryChannelsTableWrapper} ref={scrollRef}>
              {content}
            </div>
            <Outlet />
          </div>
        </div>
      );
    } else {
      globalContent = <PermissionDenied />;
    }
  }

  return globalContent;
};

export default DeliveryChannels;
